import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNotification, DeleteConfirmationModal } from '../common';

// Helper function to get month name from number
const getMonthName = (monthNum) => {
  if (!monthNum) return '';
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Convert to number and subtract 1 as arrays are 0-indexed
  const index = parseInt(monthNum) - 1;
  return monthNames[index] || `Month ${monthNum}`;
};

const ContractorInvoices = ({ employees }) => {
  const [invoices, setInvoices] = useState([]);
  const [teachingHours, setTeachingHours] = useState([]);
  const [invoicesError, setInvoicesError] = useState('');
  const [tutorInvoicesLoading, setTutorInvoicesLoading] = useState(false);
  const [allContractors, setAllContractors] = useState([]);
  const [invoiceForm, setInvoiceForm] = useState({
    tutorId: '',
    month: '',
    year: ''
  });
  const [hoursForm, setHoursForm] = useState({
    tutorId: '',
    date: new Date().toISOString().split('T')[0],
    hours: '',
    description: ''
  });
  const [manualHoursEntry, setManualHoursEntry] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const { showNotification } = useNotification();
  
  // Define handlers for form changes
  const handleInvoiceFormChange = (e) => {
    const { name, value } = e.target;
    setInvoiceForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleHoursFormChange = (e) => {
    const { name, value } = e.target;
    setHoursForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Fetch all contractors for dropdown (without pagination)
  const fetchAllContractors = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }
    
    axios.get('/api/employees', {
      headers: {
        'x-auth-token': token
      },
      params: {
        limit: 1000, // High limit to get all contractors
        page: 1
      }
    })
    .then(response => {
      setAllContractors(response.data.users || []);
    })
    .catch(error => {
      console.error('Error fetching contractors:', error);
    });
  };

  // Fetch invoices from the API
  const fetchInvoices = () => {
    console.log('Fetching invoices...');
    setTutorInvoicesLoading(true);
    
    const token = localStorage.getItem('token');
    if (!token) {
      setTutorInvoicesLoading(false);
      setInvoicesError('Authentication token missing. Please log in again.');
      return;
    }
    
    axios.get('/api/employees/invoices', {
      headers: {
        'x-auth-token': token
      }
    })
    .then(response => {
      console.log('Invoices fetched successfully:', response.data);
      
      // Add month name to each invoice for better display
      const processedInvoices = response.data.map(invoice => ({
        ...invoice,
        // Add monthName if not already present
        monthName: invoice.monthName || getMonthName(invoice.month)
      }));
      
      setInvoices(processedInvoices);
      setTutorInvoicesLoading(false);
    })
    .catch(error => {
      console.error('Error fetching invoices:', error);
      setTutorInvoicesLoading(false);
      setInvoicesError('Failed to fetch invoices. Please try again.');
    });
  };
  
  // Generate invoice for the selected tutor
  const generateInvoice = () => {
    console.log('Generating invoice...');
    // Validate form data
    if (!invoiceForm.tutorId || !invoiceForm.month || !invoiceForm.year) {
      showNotification('Please select a tutor, month, and year', 'error');
      return;
    }
    
    setTutorInvoicesLoading(true);
    
    // Find the tutor by ID - using string comparison for safety
    const tutor = allContractors.find(emp => emp.id.toString() === invoiceForm.tutorId.toString());
    const tutorName = tutor ? tutor.name : 'Unknown Tutor';
    
    console.log('Invoice for tutor:', { 
      looking_for_id: invoiceForm.tutorId,
      found_tutor: tutor,
      resulting_name: tutorName
    });
    
    // Calculate total hours from teaching hours records for this tutor and month
    const selectedMonth = parseInt(invoiceForm.month);
    const selectedYear = parseInt(invoiceForm.year);
    
    // Filter teaching hours for this tutor and the selected month
    const tutorHoursForMonth = teachingHours.filter(record => {
      // Must match the tutorId
      if (record.tutorId.toString() !== invoiceForm.tutorId.toString()) return false;
      
      // Must be in the selected month and year
      const recordDate = new Date(record.date);
      return recordDate.getMonth() + 1 === selectedMonth && 
             recordDate.getFullYear() === selectedYear;
    });
    
    // Calculate total hours
    const totalHours = tutorHoursForMonth.reduce((sum, record) => sum + record.hours, 0);
    
    // Calculate amount based on hourly rate if available, otherwise use a default rate
    const hourlyRate = tutor?.hourly_rate || 300; // Default rate if not specified
    const total = totalHours * hourlyRate;
    
    // Determine currency based on tutor's country
    const currency = tutor?.country === 'Norway' ? 'NOK' : 
                    tutor?.country === 'Sweden' ? 'SEK' : 'EUR';
    
    // Get token for API authentication
    const token = localStorage.getItem('token');
    if (!token) {
      setTutorInvoicesLoading(false);
      showNotification('Authentication token missing. Please log in again.', 'error');
      return;
    }
    
    // Prepare invoice data for API
    const invoiceData = {
      tutorId: invoiceForm.tutorId,
      tutor_name: tutorName, // Use tutor_name to match the backend field name
      month: parseInt(invoiceForm.month),
      year: parseInt(invoiceForm.year),
      hours: parseFloat(totalHours.toFixed(1)),
      hourlyRate: hourlyRate,
      total: Math.round(total), // Round to nearest whole currency unit
      currency: currency,
      // Include the hour records for detailed line items
      hourRecords: tutorHoursForMonth.map(record => ({
        date: record.date,
        hours: record.hours,
        description: record.description,
        fromCalendar: record.id.startsWith('cal-') // Track if this was from calendar or manual entry
      }))
    };
    
    console.log(`Using tutor name "${tutorName}" for invoice creation`);
    
    console.log('Sending invoice data to API:', invoiceData);
    
    // Call the API to create the invoice
    axios.post('/api/employees/invoices', invoiceData, {
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json'
      }
    })
    .then(response => {
      console.log('Invoice created successfully:', response.data);
      
      // Format the response data
      const newInvoice = {
        ...response.data,
        monthName: new Date(response.data.year, response.data.month - 1, 1).toLocaleString('default', { month: 'long' })
      };
      
      // Add the new invoice to the list
      setInvoices(prev => [newInvoice, ...prev]);
      setTutorInvoicesLoading(false);
      
      // Reset the form
      setInvoiceForm({
        tutorId: '',
        month: '',
        year: ''
      });
      
      showNotification('Invoice generated and saved successfully!', 'success');
    })
    .catch(error => {
      console.error('Error creating invoice:', error);
      setTutorInvoicesLoading(false);
      
      // Show detailed error message if available
      if (error.response && error.response.data && error.response.data.message) {
        showNotification(`Error creating invoice: ${error.response.data.message}`, 'error');
      } else {
        showNotification('Failed to create invoice. Please try again.', 'error');
      }
    });
  };
  
  // Handle recording or fetching teaching hours
  const handleRecordHours = () => {
    // Validate form data
    if (!hoursForm.tutorId || !hoursForm.date) {
      showNotification('Please select a tutor and date', 'error');
      return;
    }
    
    if (manualHoursEntry && !hoursForm.hours) {
      showNotification('Please enter the number of hours', 'error');
      return;
    }
    
    setTutorInvoicesLoading(true);
    
    // Find the tutor name
    const tutor = allContractors.find(emp => emp.id.toString() === hoursForm.tutorId.toString());
    const tutorName = tutor ? tutor.name : 'Unknown Tutor';
    
    // Log for debugging
    console.log('Tutor lookup:', { 
      looking_for_id: hoursForm.tutorId,
      found_tutor: tutor,
      resulting_name: tutorName,
      available_employees: allContractors.map(e => ({ id: e.id, name: e.name, role: e.role }))
    });
    
    if (manualHoursEntry) {
      // Add the manually entered hours
      const newRecord = {
          id: Date.now().toString(), // Use timestamp as ID
          tutorId: hoursForm.tutorId,
          tutor_name: tutorName, // Use tutor_name to be consistent with database
          date: hoursForm.date,
          hours: parseFloat(hoursForm.hours),
          description: hoursForm.description || 'Teaching session'
        };
        
      console.log('Creating manual record:', newRecord);
        
        setTeachingHours(prev => [newRecord, ...prev]);
        
        // Reset the hours input but keep the tutor and date
        setHoursForm(prev => ({
          ...prev,
          hours: '',
          description: ''
        }));
        
        setTutorInvoicesLoading(false);
        showNotification('Teaching hours recorded successfully!', 'success');
    } else {
      // Auto mode: Fetch teaching hours from calendar events
      const selectedDate = new Date(hoursForm.date);
      const month = selectedDate.getMonth() + 1; // JavaScript months are 0-indexed, API expects 1-indexed
      const year = selectedDate.getFullYear();
      
      // Get the token for API authentication
      const token = localStorage.getItem('token');
      if (!token) {
        setTutorInvoicesLoading(false);
        showNotification('Authentication token missing. Please log in again.', 'error');
        return;
      }
      
      // Call the API to get calendar events for this tutor
      axios.get('/api/calendar/events/tutor', {
        headers: {
          'x-auth-token': token
        },
        params: {
          tutorId: hoursForm.tutorId,
          month: month,
          year: year,
          type: 'course' // Only get course events
        }
      })
      .then(response => {
        const calendarEvents = response.data.events || [];
        
        // Get tutor data from response if available
        const tutorData = response.data.tutor || {};
        // Use API-provided tutor name, or fallback to the one we have from the component
        const actualTutorName = tutorData.name || tutorName;
        
        // Map calendar events to teaching hours records
        const newSessions = calendarEvents
          .filter(event => {
            // Case insensitive comparison for status
            const status = (event.status || '').toLowerCase();
            // Include all events, but you can filter by status if needed
            // For example, to only include completed events:
            return status === 'completed' || status === 'complete';
          })
          .map(event => {
            // Calculate hours from event duration
            const startTime = new Date(event.start_time);
            const endTime = new Date(event.end_time);
            const durationHours = (endTime - startTime) / (1000 * 60 * 60); // Convert ms to hours
            
            return {
              id: `cal-${event.id}`,
              tutorId: hoursForm.tutorId,
              tutor_name: actualTutorName, // Use tutor_name to be consistent with database
              date: startTime.toISOString().split('T')[0],
              hours: parseFloat(durationHours.toFixed(1)), // Round to 1 decimal place
              description: `${event.title || 'Course Session'} - ${event.course_name || ''}`
            };
          });
          
        // Sort by date (newest first)
        newSessions.sort((a, b) => new Date(b.date) - new Date(a.date));
        
        // Add the new sessions to the existing ones
        setTeachingHours(prev => [...newSessions, ...prev]);
        setTutorInvoicesLoading(false);
        
        // Show appropriate message based on results
        if (newSessions.length > 0) {
          showNotification(`${newSessions.length} teaching sessions fetched from calendar!`, 'success');
        } else if (calendarEvents.length > 0) {
          showNotification('Calendar events found, but none with "Completed" status. Only completed events are included in teaching hours.', 'info');
        } else {
          showNotification('No calendar events found for this tutor in the selected period.', 'info');
        }
      })
      .catch(error => {
        console.error('Error fetching calendar events:', error);
        setTutorInvoicesLoading(false);
        showNotification('Failed to fetch teaching hours from calendar. Please try again.', 'error');
      });
    }
  };
  
  // Initial fetch when component mounts
  useEffect(() => {
    // Fetch invoices on component mount
    fetchInvoices();
    
    // Fetch all contractors for dropdowns
    fetchAllContractors();
    
    // Log employees for debugging
    console.log('Employees available in TutorInvoices:', employees);
  }, []);

  return (
    <div>
      {tutorInvoicesLoading ? (
        <div className="text-center p-8">
          <div className="animate-pulse">
            <div className="h-12 bg-gray-200 rounded mb-4"></div>
            <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
            <div className="h-8 bg-gray-200 rounded w-1/2 mx-auto mb-2"></div>
            <div className="h-24 bg-gray-200 rounded mb-4"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
          <p className="mt-4 text-gray-600">Loading contractor invoices data...</p>
          <button 
            onClick={() => fetchInvoices()} 
            className="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Click here to retry
          </button>
        </div>
      ) : invoicesError ? (
        <div className="text-center p-8">
          <div className="text-red-500 mb-4 text-xl">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Error loading contractor invoices
          </div>
          <p className="mb-4 text-gray-700">{invoicesError}</p>
          <div className="flex justify-center space-x-4">
            <button 
              onClick={() => fetchInvoices()} 
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      ) : (
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold">Contractor Invoices</h3>
              <p className="text-sm text-gray-500">Generate and manage invoices for contractors based on their hours</p>
            </div>
            <div className="flex space-x-2">
              <button 
                className="flex items-center px-3 py-2 bg-indigo-600 text-white rounded-md shadow-sm hover:bg-indigo-700"
                title="Export invoices to CSV file"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Export Invoices
              </button>
            </div>
          </div>
      
          {/* Invoice Generation Form */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h4 className="text-lg font-medium mb-4">Generate New Invoice</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contractor
                </label>
                <select
                  name="tutorId"
                  value={invoiceForm.tutorId}
                  onChange={handleInvoiceFormChange}
                  className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a Contractor</option>
                  {allContractors
                    .map(tutor => (
                      <option key={tutor.id} value={tutor.id.toString()}>
                        {tutor.name} - {tutor.country || 'Unknown country'} {tutor.hourly_rate ? `(${tutor.hourly_rate} ${tutor.country === 'Norway' ? 'NOK' : tutor.country === 'Sweden' ? 'SEK' : 'EUR'}/hr)` : ''}
                      </option>
                    ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Month
                </label>
                <select
                  name="month"
                  value={invoiceForm.month}
                  onChange={handleInvoiceFormChange}
                  className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Month</option>
                  <option value="1">January</option>
                  <option value="2">February</option>
                  <option value="3">March</option>
                  <option value="4">April</option>
                  <option value="5">May</option>
                  <option value="6">June</option>
                  <option value="7">July</option>
                  <option value="8">August</option>
                  <option value="9">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year
                </label>
                <select
                  name="year"
                  value={invoiceForm.year}
                  onChange={handleInvoiceFormChange}
                  className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Year</option>
                  <option value="2023">2023</option>
                  <option value="2024">2024</option>
                  <option value="2025">2025</option>
                </select>
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <button
                onClick={generateInvoice}
                disabled={tutorInvoicesLoading || !invoiceForm.tutorId || !invoiceForm.month || !invoiceForm.year}
                className={`px-4 py-2 bg-green-600 text-white rounded-md shadow-sm hover:bg-green-700 ${
                  (tutorInvoicesLoading || !invoiceForm.tutorId || !invoiceForm.month || !invoiceForm.year) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {tutorInvoicesLoading ? 'Working...' : 'Generate Invoice'}
              </button>
            </div>
          </div>
          
          {/* Invoices Table */}
          <div className="bg-white shadow-md rounded-lg overflow-hidden mb-6">
            <div className="p-4 border-b">
              <h4 className="text-lg font-medium">Recent Invoices</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contractor
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Period
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hours
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rate
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoices.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-4 text-center text-sm text-gray-500">
                        No invoices found. Generate your first invoice using the form above.
                      </td>
                    </tr>
                  ) : (
                    invoices.map(invoice => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {invoice.id || `INV-${invoice.year}-${String(invoice.month).padStart(2, '0')}-${invoice.tutorId}`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {invoice.tutor_name || 'No tutor name'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {invoice.monthName || getMonthName(invoice.month)} {invoice.year}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {invoice.hours || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {invoice.hourlyRate} {invoice.currency}/hr
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {invoice.total?.toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          })} {invoice.currency}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            invoice.paid ? 
                            'bg-green-100 text-green-800' : 
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {invoice.status || (invoice.paid ? 'Paid' : 'Pending')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button 
                            className="text-blue-600 hover:text-blue-900 mr-3"
                            onClick={() => {
                              // View functionality could be added in the future
                              showNotification('View functionality will be implemented in a future update', 'info');
                            }}
                          >
                            View
                          </button>
                          <button 
                            className="text-blue-600 hover:text-blue-900 mr-3"
                            onClick={() => {
                              // Download the invoice PDF
                              const token = localStorage.getItem('token');
                              if (!token) {
                                showNotification('Authentication token missing. Please log in again.', 'error');
                                return;
                              }
                              
                              // Create URL with the invoice ID
                              const downloadUrl = `/api/employees/invoices/${invoice.id}/pdf`;
                              
                              // Create temporary link element
                              const link = document.createElement('a');
                              link.href = downloadUrl;
                              
                              // Set Authorization header using the download attribute
                              // This won't work directly, but we'll use a workaround
                              
                              // Use the exact same approach as InvoicesView.js with the backend URL
                              console.log(`Preparing download for tutor invoice ID: ${invoice.id}`);
                              
                              // Important: use the backend URL directly (port 5005, not 3000)
                              const backendUrl = 'http://localhost:5005';
                              
                              // Create a form and submit it to download the PDF
                              const form = document.createElement('form');
                              form.method = 'GET';  // Using GET since our endpoint uses GET
                              form.action = `${backendUrl}/api/employees/invoices/${invoice.id}/pdf`;
                              form.target = '_blank';
                              
                              // Add the token as a hidden field
                              const tokenInput = document.createElement('input');
                              tokenInput.type = 'hidden';
                              tokenInput.name = 'token';
                              tokenInput.value = token;
                              form.appendChild(tokenInput);
                              
                              // Add the form to the body and submit it
                              document.body.appendChild(form);
                              form.submit();
                              
                              // Clean up
                              setTimeout(() => {
                                document.body.removeChild(form);
                              }, 1000);
                              
                              // Method 2: Alternative - create form and submit it
                              /*
                              const form = document.createElement('form');
                              form.method = 'GET';
                              form.action = downloadUrl;
                              form.target = '_blank';
                              
                              const tokenInput = document.createElement('input');
                              tokenInput.type = 'hidden';
                              tokenInput.name = 'token';
                              tokenInput.value = token;
                              
                              form.appendChild(tokenInput);
                              document.body.appendChild(form);
                              form.submit();
                              document.body.removeChild(form);
                              */
                            }}
                          >
                            Download
                          </button>
                          <button 
                            className="text-red-600 hover:text-red-900"
                            onClick={() => {
                              setInvoiceToDelete(invoice.id);
                              setShowDeleteConfirm(true);
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Teaching Hours Entry Section */}
          <div className="bg-white shadow-md rounded-lg mb-4">
            <div className="p-4 border-b">
              <h4 className="text-lg font-medium">Record Hours</h4>
              <p className="text-sm text-gray-500">Enter hours for contractors to be included in their invoices</p>
              
              {/* Mode Toggle */}
              <div className="mt-3 flex items-center">
                <span className="text-sm font-medium text-gray-700 mr-2">
                  Source:
                </span>
                <div className="relative inline-flex items-center mr-2">
                  <div className={`w-10 h-5 transition-colors duration-200 ease-in-out rounded-full ${manualHoursEntry ? 'bg-gray-200' : 'bg-green-500'}`}>
                    <div 
                      className={`absolute left-0 top-0 transform transition-transform duration-200 ease-in-out w-5 h-5 rounded-full bg-white shadow ${manualHoursEntry ? 'translate-x-0' : 'translate-x-5'}`}
                    ></div>
                  </div>
                  <button 
                    onClick={() => setManualHoursEntry(!manualHoursEntry)} 
                    className="absolute inset-0 w-full h-full cursor-pointer"
                    aria-pressed={!manualHoursEntry}
                    aria-label="Toggle hours entry source"
                  ></button>
                </div>
                <span className={`text-sm ${!manualHoursEntry ? 'font-semibold text-green-600' : 'text-gray-500'}`}>
                  Auto (from calendar)
                </span>
                <span className="mx-1 text-gray-400">|</span>
                <span className={`text-sm ${manualHoursEntry ? 'font-semibold text-blue-600' : 'text-gray-500'}`}>
                  Manual entry
                </span>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contractor
                  </label>
                  <select
                    name="tutorId"
                    value={hoursForm.tutorId}
                    onChange={handleHoursFormChange}
                    className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a Contractor</option>
                                    {/* Check for contractor role with flexible matching */}
                    {allContractors
                      .map(tutor => (
                        <option key={tutor.id} value={tutor.id.toString()}>
                          {tutor.name} - {tutor.country || 'Unknown country'}
                        </option>
                      ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {manualHoursEntry ? 'Date' : 'Month/Year Reference'}
                  </label>
                  <input 
                    type="date"
                    name="date"
                    value={hoursForm.date}
                    onChange={handleHoursFormChange}
                    className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  {!manualHoursEntry && (
                    <p className="text-xs text-gray-500 mt-1">
                      We'll fetch all completed sessions for the selected month
                    </p>
                  )}
                </div>
                
                {manualHoursEntry && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hours
                    </label>
                    <input 
                      type="number" 
                      name="hours"
                      value={hoursForm.hours}
                      onChange={handleHoursFormChange}
                      min="0.5" 
                      step="0.5" 
                      placeholder="Enter hours" 
                      className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}
              </div>
              
              {manualHoursEntry && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={hoursForm.description}
                    onChange={handleHoursFormChange}
                    placeholder="Enter details about the teaching session"
                    rows="3"
                    className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  ></textarea>
                </div>
              )}
              
              {!manualHoursEntry && (
                <div className="mt-2 bg-blue-50 p-3 rounded-md">
                  <div className="flex items-center text-blue-700">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium">Automatic Hours</span>
                  </div>
                  <p className="text-sm text-blue-700 mt-1 ml-7">
                    Hours will be calculated from completed calendar events where this contractor is assigned and the event type is "course".
                  </p>
                </div>
              )}
              
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handleRecordHours}
                  disabled={tutorInvoicesLoading || !hoursForm.tutorId || (!hoursForm.date) || (manualHoursEntry && !hoursForm.hours)}
                  className={`px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 ${
                    (tutorInvoicesLoading || !hoursForm.tutorId || (!hoursForm.date) || (manualHoursEntry && !hoursForm.hours)) ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {tutorInvoicesLoading ? 'Working...' : (manualHoursEntry ? 'Record Hours' : 'Fetch Hours from Calendar')}
                </button>
              </div>
              
              {/* Teaching Hours Records */}
              <div className="mt-6 border-t pt-4">
                <h5 className="text-sm font-medium text-gray-700 mb-3">Recent Hours</h5>
                {teachingHours.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Contractor
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Hours
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Description
                          </th>
                          <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {teachingHours.map(record => (
                          <tr key={record.id}>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{record.tutor_name}</div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{new Date(record.date).toLocaleDateString()}</div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{record.hours}</div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="text-sm text-gray-900 truncate max-w-xs">{record.description}</div>
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap text-sm font-medium">
                              <button
                                className="text-red-600 hover:text-red-900"
                                onClick={() => {
                                  setTeachingHours(teachingHours.filter(r => r.id !== record.id));
                                }}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center p-8 bg-gray-50 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-gray-600 mb-2">No hours recorded yet</p>
                    <p className="text-sm text-gray-500">
                      Use the form above to manually record hours or fetch them from the calendar.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          // Delete from the database
          const token = localStorage.getItem('token');
          if (!token) {
            showNotification('Authentication token missing. Please log in again.', 'error');
            return;
          }
                                
          // Show loading state
          setTutorInvoicesLoading(true);
                                
          axios.delete(`/api/employees/invoices/${invoiceToDelete}`, {
            headers: {
              'x-auth-token': token
            }
          })
          .then(response => {
            console.log('Invoice deleted successfully:', response.data);
            
            // Update state to remove the invoice from the list
            setInvoices(invoices.filter(inv => inv.id !== invoiceToDelete));
            setTutorInvoicesLoading(false);
            showNotification('Invoice deleted successfully', 'success');
            setShowDeleteConfirm(false);
          })
          .catch(error => {
            console.error('Error deleting invoice:', error);
            setTutorInvoicesLoading(false);
            showNotification('Failed to delete invoice. Please try again.', 'error');
          });
        }}
        message="Are you sure you want to delete this invoice? This action cannot be undone."
      />
    </div>
  );
};

export default ContractorInvoices;